/**
 * Custom hook for fetching and managing prehire data
 */

import { useState, useEffect, useCallback } from 'react';
import { useMsal } from '@azure/msal-react';
import { SeverityLevel } from '@microsoft/applicationinsights-web';
import { PrehireDto, ApiException } from '../../generated/api.onboarding.client';
import { createPrehireClient } from '../api/apiClient';
import { useNoAccess } from '../../context';
import { isTestMode, getAuthProvider } from '../../config';
import { appInsights } from '../../telemetry/appInsights';
import { getImpersonationEmail } from '../../utils/impersonation';

/**
 * Error messages for different scenarios
 */
const ERROR_MESSAGES = {
  // MSFT employee with no hire data (corporate login, 404)
  MSFT_NO_HIRE_DATA:
    "Access to this site is limited to candidates who have accepted an offer via a requisition.\n\nWe can't locate an offer acceptance linked to your profile right now. If you recently accepted your offer, please wait up to 4 hours and try again.",

  // Candidate uses wrong/different account OR no account record (B2C login, 404)
  WRONG_ACCOUNT_OR_NO_RECORD:
    'We are unable to locate an offer acceptance associated with the email address used for sign-in. Please ensure you are using the same email address registered on Microsoft Careers. If the offer was recently accepted, allow up to 4 hours for the information to be updated.',

  // Case resolved or offer declined (403 or specific error code)
  NO_ACTIVE_OFFER:
    "**We couldn't find your account.**\n\n- Recently accepted an offer? Your information may take up to 48 hours to become available. Please try again later.\n- Access to this portal is limited to eligible users during the onboarding process.\n- Access is no longer available two weeks after your start date.",

  // General error / data loading issues (500 or other errors)
  GENERAL_ERROR: 'We ran into an issue while processing your request. Please try again later.',

  IMPERSONATION_INVALID_EMAIL: 'Enter a valid candidate email address and try again.',
  IMPERSONATION_FORBIDDEN: 'You do not have permission to view this candidate.',
  IMPERSONATION_NOT_FOUND: 'We could not locate candidate data for the requested email address.',
};

interface UsePrehireDataResult {
  data: PrehireDto | null;
  loading: boolean;
  error: string | null;
  refetch: () => Promise<void>;
}

/**
 * Parse API error response to determine specific error type
 */
const parseErrorResponse = (response: string): { errorCode?: string } => {
  try {
    const parsed = JSON.parse(response);
    return { errorCode: parsed.errorCode || parsed.code || parsed.error };
  } catch {
    return {};
  }
};

const getErrorStatus = (error: unknown): number | undefined => {
  if (ApiException.isApiException(error)) {
    return error.status;
  }

  if (typeof error === 'object' && error !== null && 'status' in error && typeof error.status === 'number') {
    return error.status;
  }

  return undefined;
};

/**
 * Hook to fetch prehire details for the authenticated user
 * In test mode, bypasses MSAL authentication
 */
export const usePrehireData = (): UsePrehireDataResult => {
  const msalContext = useMsal();
  const noAccessContext = useNoAccess();

  // In test mode, we don't have MSAL context
  const instance = isTestMode ? null : msalContext?.instance;
  const accounts = isTestMode ? [] : msalContext?.accounts || [];

  const [data, setData] = useState<PrehireDto | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  const account = accounts[0] || null;
  const impersonationEmail = getImpersonationEmail();

  const fetchData = useCallback(async () => {
    // In test mode, we can proceed without an account
    if (!isTestMode && !account) {
      setLoading(false);
      setError('No authenticated account found');
      return;
    }

    setLoading(true);
    setError(null);
    if (impersonationEmail) {
      setData(null);
    }

    try {
      const client = createPrehireClient(instance, account);
      const prehireData = impersonationEmail ? await client.impersonate(impersonationEmail) : await client.prehire();
      setData(prehireData);
    } catch (err) {
      console.error('Error fetching prehire data:', err);

      // Track error in Application Insights
      const error = err instanceof Error ? err : new Error(String(err));
      appInsights.trackException({
        exception: error,
        severityLevel: SeverityLevel.Error,
        properties: {
          component: 'usePrehireData',
          action: 'fetchPrehireData',
          statusCode: String(getErrorStatus(err) ?? 'unknown'),
          authProvider: isTestMode ? 'test' : getAuthProvider(),
          isImpersonating: Boolean(impersonationEmail),
        },
      });

      if (impersonationEmail) {
        const status = getErrorStatus(err);

        switch (status) {
          case 400:
            setError(ERROR_MESSAGES.IMPERSONATION_INVALID_EMAIL);
            break;
          case 403:
            setError(ERROR_MESSAGES.IMPERSONATION_FORBIDDEN);
            break;
          case 404:
            setError(ERROR_MESSAGES.IMPERSONATION_NOT_FOUND);
            break;
          case 401:
            setError('Session expired. Please refresh the page.');
            break;
          default:
            setError(
              typeof err === 'string' && err.toLowerCase().includes('not found')
                ? ERROR_MESSAGES.IMPERSONATION_NOT_FOUND
                : ERROR_MESSAGES.GENERAL_ERROR
            );
            break;
        }
        return;
      }

      if (ApiException.isApiException(err)) {
        const apiError = err as ApiException;
        const { errorCode: _errorCode } = parseErrorResponse(apiError.response);

        switch (apiError.status) {
          case 404: {
            // No data found - determine message based on auth provider and error code
            if (!isTestMode && noAccessContext?.onNoAccess) {
              const authProvider = getAuthProvider();

              // If corporate employee, show MSFT-specific message
              if (authProvider === 'corporate') {
                noAccessContext.onNoAccess(ERROR_MESSAGES.MSFT_NO_HIRE_DATA);
              } else {
                // B2C user - wrong account or no record
                noAccessContext.onNoAccess(ERROR_MESSAGES.WRONG_ACCOUNT_OR_NO_RECORD);
              }
            }
            return;
          }
          case 403: {
            // Forbidden - case resolved or offer declined
            if (!isTestMode && noAccessContext?.onNoAccess) {
              noAccessContext.onNoAccess(ERROR_MESSAGES.NO_ACTIVE_OFFER);
            }
            return;
          }
          case 401:
            setError('Session expired. Please refresh the page.');
            break;
          case 500:
          default:
            // General error - trigger logout with general error message
            if (!isTestMode && noAccessContext?.onNoAccess) {
              noAccessContext.onNoAccess(ERROR_MESSAGES.GENERAL_ERROR);
            }
            return;
        }
      } else if (err instanceof Error) {
        // Network or other errors - show general error
        if (!isTestMode && noAccessContext?.onNoAccess) {
          noAccessContext.onNoAccess(ERROR_MESSAGES.GENERAL_ERROR);
        }
      } else {
        if (!isTestMode && noAccessContext?.onNoAccess) {
          noAccessContext.onNoAccess(ERROR_MESSAGES.GENERAL_ERROR);
        }
      }
    } finally {
      setLoading(false);
    }
  }, [instance, account, noAccessContext, impersonationEmail]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  return {
    data,
    loading,
    error,
    refetch: fetchData,
  };
};
